import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Box, Chip, Divider, Paper, Stack, TextField, Typography,
} from "@mui/material";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import DownloadIcon from "@mui/icons-material/Download";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

import FormField from "../../../component/common/FormField";
import ComboBox from "../../../component/common/ComboBox";
import ActionButton from "../../../component/common/ActionButton";
import ResizableTable from "../../../component/common/ResizableTable";
import TablePaginator from "../../../component/common/TablePaginator";
import LoadingDots from "../../../component/common/LoadingDots";
import { TableCell, TableHead, TableRow, TableBody } from "@mui/material";

import { ds } from "../../../styles/designSystem";
import { notify } from "../../../util/toast";
import { runApiAction, serverMsgOr } from "../../../util/apiFeedback";
import { today as getToday } from "../../../util/dateUtils";
import { hrApi, saveCertificatePdf, readBlobError, CERTIFICATE_TYPE } from "../api/hrApi";

const PURPOSE_MAX = 60;

/** 발급 폼 초기값 — 발급 후에도 여기로 되돌린다(직원만 그대로 두면 연속 발급이 편하다). */
const emptyForm = () => ({
    employeeId: "",
    rrnFront: "",
    type: "EMPLOYMENT",
    purpose: "",
    issuedDate: getToday().format("YYYY-MM-DD"),
    useCustomAddress: false,
    addressOverride: "",
});

const fmtDate = (d) => (d ? String(d).replace(/-/g, ". ") : "—");

const fmtDateTime = (dt) => {
    if (!dt) return "—";
    const d = new Date(dt);
    if (Number.isNaN(d.getTime())) return "—";
    return `${d.getFullYear()}. ${String(d.getMonth() + 1).padStart(2, "0")}. ${String(d.getDate()).padStart(2, "0")}`
        + ` ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};

/** 재직기간 문구 — PDF 와 같은 규칙(퇴사일이 없으면 재직중). */
const periodOf = (emp) => {
    if (!emp?.joinDate) return "—";
    return `${fmtDate(emp.joinDate)} ~ ${emp.retireDate ? fmtDate(emp.retireDate) : "재직중"}`;
};

/** 발급을 막아야 하는 이유를 한 줄로. 없으면 null — 버튼 활성 조건과 안내 문구가 한 곳에서 나온다. */
function blockingReason(emp, type, form) {
    if (!emp) return "증명 대상 직원을 선택하세요.";
    if (!emp.joinDate) return `${emp.name} 님의 입사일이 등록되어 있지 않습니다. 직원 관리에서 입사일을 먼저 입력하세요.`;
    if (type === "EMPLOYMENT" && emp.status === "RETIRED")
        return `${emp.name} 님은 퇴사 처리된 직원이라 재직증명서를 발급할 수 없습니다. 경력증명서를 선택하세요.`;
    if (!/^\d{6}$/.test(form.rrnFront || ""))
        return "주민등록번호 앞 6자리(생년월일)를 입력하세요. 예: 890331";
    const address = form.useCustomAddress ? form.addressOverride : emp.address;
    if (!address || !address.trim())
        return `${emp.name} 님의 주소가 등록되어 있지 않습니다. 직원 관리에서 입력하거나, 아래에서 이번 발급에 쓸 주소를 직접 입력하세요.`;
    if (!form.purpose.trim()) return "용도를 입력하세요.";
    return null;
}

/** 발급 폼 안의 "이렇게 나갑니다" 미리보기 한 줄. */
function PreviewRow({ label, value, muted }) {
    return (
        <Box sx={{ display: "flex", gap: 1.5, py: 0.4 }}>
            <Typography sx={{ fontSize: 12, color: ds.colors.textMuted, width: 76, flexShrink: 0 }}>
                {label}
            </Typography>
            <Typography sx={{
                fontSize: 12.5, fontWeight: 500,
                color: muted ? ds.colors.textMuted : ds.colors.textPrimary,
            }}>
                {value || "—"}
            </Typography>
        </Box>
    );
}

/**
 * 인사 > 인사관리 > 증명서 발급.
 *
 * <h3>화면이 이렇게 생긴 이유</h3>
 * <p>인사팀이 이 화면에서 하는 일은 사실상 하나다 — <b>직원을 고르고, 용도를 적고, 받는다.</b>
 * 그래서 위쪽은 그 세 동작만 남기고, 나머지(소속·직위·입사일·재직기간·주민번호 앞자리·주소)는
 * 직원을 고르는 순간 <b>문서에 나갈 그대로</b> 미리보기로 보여준다. 발급 버튼을 누르기 전에
 * 틀린 값을 발견하게 하려는 것이다 — 잘못 나간 증명서는 회수할 수 없다.
 *
 * <p>아래쪽 이력은 "지난주에 김세영 님 것 뽑아 드렸는데 다시 필요하대요" 를 위한 것이다. 행을 누르면
 * 발급 당시 내용 그대로 사본을 받는다(새 이력이 생기지 않는다).
 */
export default function HrCertificateTab() {
    const [candidates, setCandidates] = useState([]);
    const [candidatesLoading, setCandidatesLoading] = useState(true);
    const [form, setForm] = useState(emptyForm);
    const [issuing, setIssuing] = useState(false);

    const [history, setHistory] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(true);
    const [typeFilter, setTypeFilter] = useState("ALL");
    const [page, setPage] = useState(1);          // TablePaginator 는 1-based
    const [pageSize] = useState(10);
    const [totalPages, setTotalPages] = useState(1);
    const [totalElements, setTotalElements] = useState(0);
    const [downloadingId, setDownloadingId] = useState(null);

    const selected = useMemo(
        () => candidates.find(c => String(c.id) === String(form.employeeId)) || null,
        [candidates, form.employeeId]
    );

    // ── 로딩 ────────────────────────────────────────────────────────────────
    useEffect(() => {
        let alive = true;
        setCandidatesLoading(true);
        // http.js 의 응답 인터셉터가 axios 응답 껍데기를 이미 벗겨 준다 — 여기 오는 건 본문(배열) 자체다.
        // res.data 로 읽으면 undefined 가 되어 "목록이 비었다" 와 구분이 안 된다(실제로 그렇게 비어 보였다).
        hrApi.getCertificateCandidates()
            .then(list => { if (alive) setCandidates(Array.isArray(list) ? list : []); })
            .catch(() => { if (alive) notify("직원 목록을 불러오지 못했습니다.", "error"); })
            .finally(() => { if (alive) setCandidatesLoading(false); });
        return () => { alive = false; };
    }, []);

    const loadHistory = useCallback(() => {
        setHistoryLoading(true);
        const params = { page: page - 1, size: pageSize };
        if (typeFilter !== "ALL") params.type = typeFilter;
        hrApi.getCertificates(params)
            .then(res => {
                const d = res || {};   // 본문(PagedResponse) 이 그대로 온다 — 위 후보 목록과 같은 이유.
                setHistory(d.content || []);
                setTotalPages(d.totalPages || 1);
                setTotalElements(d.totalElements || 0);
            })
            .catch(() => notify("발급 이력을 불러오지 못했습니다.", "error"))
            .finally(() => setHistoryLoading(false));
    }, [page, pageSize, typeFilter]);

    useEffect(() => { loadHistory(); }, [loadHistory]);

    // ── 동작 ────────────────────────────────────────────────────────────────
    const reason = blockingReason(selected, form.type, form);

    const handleIssue = async () => {
        if (reason) { notify(reason, "warning"); return; }
        setIssuing(true);
        try {
            const res = await hrApi.issueCertificate({
                employeeId: selected.id,
                type: form.type,
                purpose: form.purpose.trim(),
                issuedDate: form.issuedDate || null,
                rrnFrontOverride: form.rrnFront,
                addressOverride: form.useCustomAddress ? form.addressOverride.trim() : null,
            });
            const certNo = saveCertificatePdf(res, `${CERTIFICATE_TYPE[form.type].label}.pdf`);
            notify(
                `${selected.name} 님 ${CERTIFICATE_TYPE[form.type].label}를 발급했습니다.`
                + (certNo ? ` (발급번호 ${certNo})` : ""),
                "success"
            );
            // 직원 선택은 남긴다 — 같은 사람의 재직+경력을 잇달아 뽑는 일이 잦다.
            setForm(p => ({ ...emptyForm(), employeeId: p.employeeId, rrnFront: p.rrnFront }));
            setPage(1);
            loadHistory();
        } catch (error) {
            // blob 요청이라 서버 메시지가 Blob 안에 들어 있다 — 풀어내지 않으면 이유가 사라진다.
            const msg = await readBlobError(error);
            notify(msg || "증명서 발급에 실패했습니다.", "error");
        } finally {
            setIssuing(false);
        }
    };

    const handleReissue = (row) => runApiAction({
        request: async () => {
            const res = await hrApi.downloadCertificate(row.id);
            saveCertificatePdf(res, `${row.typeLabel}.pdf`);
        },
        successMsg: `${row.certNo} 사본을 내려받았습니다.`,
        errorMsg: serverMsgOr("사본 다운로드에 실패했습니다."),
        setLoading: (v) => setDownloadingId(v ? row.id : null),
    });

    const addressForPreview = form.useCustomAddress ? form.addressOverride : selected?.address;

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>

            {/* ── 발급 ─────────────────────────────────────────────────── */}
            <Paper sx={ds.card}>
                <Box sx={{ px: 2.5, pt: 2, pb: 1.5, display: "flex", alignItems: "center", gap: 1 }}>
                    <DescriptionOutlinedIcon sx={{ fontSize: 18, color: ds.colors.primary }} />
                    <Typography sx={ds.typography.sectionTitle}>증명서 발급</Typography>
                    <Typography sx={{ ...ds.typography.caption, ml: 0.5 }}>
                        직원을 고르고 용도만 적으면 나머지는 등록된 직원 정보로 채워집니다
                    </Typography>
                </Box>
                <Divider />

                {candidatesLoading ? <LoadingDots /> : (
                <Box sx={{ p: 2.5, display: "grid", gridTemplateColumns: { xs: "1fr", md: "minmax(0,1fr) minmax(0,1fr)" }, gap: 2.5 }}>

                    {/* 왼쪽 — 입력 */}
                    <Stack spacing={2}>
                        <FormField label="증명 대상 직원" required>
                            <ComboBox
                                options={candidates.map(c => ({
                                    value: c.id,
                                    label: `${c.name}${c.deptName ? ` · ${c.deptName}` : ""}`
                                        + `${c.status === "RETIRED" ? " (퇴사)" : c.status === "LEAVE" ? " (휴직)" : ""}`,
                                }))}
                                value={form.employeeId}
                                // 직원을 바꾸면 주민등록번호도 그 사람 것으로 갈아 끼운다 —
                                // 앞사람 번호가 남아 있으면 남의 번호가 찍힌 증명서가 나간다.
                                onChange={v => setForm(p => ({
                                    ...p,
                                    employeeId: v,
                                    rrnFront: candidates.find(c => String(c.id) === String(v))?.rrnFront || "",
                                }))}
                                placeholder="이름으로 검색"
                            />
                        </FormField>

                        <FormField label="주민등록번호" required>
                            <TextField
                                size="small" fullWidth
                                value={form.rrnFront}
                                onChange={e => setForm(p => ({
                                    ...p, rrnFront: e.target.value.replace(/[^0-9]/g, "").slice(0, 6),
                                }))}
                                placeholder="앞 6자리 (예: 890331)"
                                inputProps={{ inputMode: "numeric", maxLength: 6 }}
                                helperText={
                                    selected && !selected.rrnFront
                                        ? "직원 정보에 생년월일이 없어 자동으로 채우지 못했습니다 — 직접 입력하세요"
                                        : "등록된 생년월일로 채워집니다 · 문서에는 앞 6자리만 인쇄됩니다"
                                }
                                FormHelperTextProps={{ sx: { fontSize: 11, ml: 0.25 } }}
                            />
                        </FormField>

                        <FormField label="종류" required>
                            <ComboBox
                                clearable={false}
                                options={[
                                    { value: "EMPLOYMENT", label: "재직증명서 — 현재 다니고 있음을 증명" },
                                    { value: "CAREER", label: "경력증명서 — 근무한 기간을 증명 (퇴사자 가능)" },
                                ]}
                                value={form.type}
                                onChange={v => setForm(p => ({ ...p, type: v }))}
                            />
                        </FormField>

                        <FormField label="용도" required>
                            <TextField
                                size="small" fullWidth
                                value={form.purpose}
                                onChange={e => setForm(p => ({ ...p, purpose: e.target.value.slice(0, PURPOSE_MAX) }))}
                                placeholder="예: 은행 제출, 비자 신청, 관공서 제출"
                                helperText={`${form.purpose.length}/${PURPOSE_MAX}자 · 문서의 '용도' 칸에 그대로 인쇄됩니다`}
                                FormHelperTextProps={{ sx: { fontSize: 11, ml: 0.25 } }}
                            />
                        </FormField>

                        <FormField label="발급일">
                            <TextField
                                size="small" fullWidth type="date"
                                value={form.issuedDate}
                                inputProps={{ max: getToday().format("YYYY-MM-DD") }}
                                onChange={e => setForm(p => ({ ...p, issuedDate: e.target.value }))}
                            />
                        </FormField>
                    </Stack>

                    {/* 오른쪽 — 문서에 나갈 내용 미리보기 */}
                    <Box sx={{
                        border: `1px solid ${ds.colors.border}`, borderRadius: 1.5,
                        bgcolor: "#fcfcfd", p: 2,
                    }}>
                        <Typography sx={{ fontSize: 12, fontWeight: 700, color: ds.colors.textSecondary, mb: 1 }}>
                            문서에 이렇게 인쇄됩니다
                        </Typography>

                        <PreviewRow label="성명" value={selected?.name} />
                        <PreviewRow label="주민등록번호" value={form.rrnFront} muted={!form.rrnFront} />
                        <PreviewRow label="주소" value={addressForPreview} muted={!addressForPreview} />
                        <PreviewRow label="소속" value={selected?.deptName} />
                        <PreviewRow label="직위" value={selected?.positionName} />
                        <PreviewRow label="입사일자" value={selected ? fmtDate(selected.joinDate) : ""} />
                        <PreviewRow label="재직기간" value={selected ? periodOf(selected) : ""} />
                        <PreviewRow label="용도" value={form.purpose} muted={!form.purpose} />

                        <Divider sx={{ my: 1.5 }} />

                        {/* 주소는 이 문서에만 다르게 쓸 일이 잦다(이사 직후 등) */}
                        <Box
                            component="label"
                            sx={{ display: "flex", alignItems: "center", gap: 0.75, cursor: "pointer", userSelect: "none" }}
                        >
                            <input
                                type="checkbox"
                                checked={form.useCustomAddress}
                                onChange={e => setForm(p => ({
                                    ...p,
                                    useCustomAddress: e.target.checked,
                                    addressOverride: e.target.checked ? (p.addressOverride || selected?.address || "") : "",
                                }))}
                            />
                            <Typography sx={{ fontSize: 12, color: ds.colors.textSecondary }}>
                                이번 발급만 다른 주소 사용
                            </Typography>
                        </Box>
                        {form.useCustomAddress && (
                            <TextField
                                size="small" fullWidth sx={{ mt: 1 }}
                                value={form.addressOverride}
                                onChange={e => setForm(p => ({ ...p, addressOverride: e.target.value }))}
                                placeholder="이번 증명서에 인쇄할 주소"
                                helperText="직원 정보의 주소는 바뀌지 않습니다"
                                FormHelperTextProps={{ sx: { fontSize: 11, ml: 0.25 } }}
                            />
                        )}
                    </Box>

                    {/* 안내 + 발급 버튼 */}
                    <Box sx={{ gridColumn: { xs: "auto", md: "1 / -1" }, display: "flex", alignItems: "center", gap: 1.5 }}>
                        {reason ? (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flex: 1 }}>
                                <WarningAmberIcon sx={{ fontSize: 16, color: ds.colors.warning }} />
                                <Typography sx={{ fontSize: 12.5, color: ds.colors.textSecondary }}>{reason}</Typography>
                            </Box>
                        ) : (
                            <Typography sx={{ fontSize: 12.5, color: ds.colors.textMuted, flex: 1 }}>
                                발급하면 A4 PDF 가 바로 내려받아지고, 아래 이력에 남습니다.
                            </Typography>
                        )}
                        <ActionButton
                            variant="primary"
                            startIcon={<DownloadIcon sx={{ fontSize: 16 }} />}
                            onClick={handleIssue}
                            loading={issuing}
                            disabled={!!reason}
                            label={issuing ? "발급 중..." : "발급하고 PDF 받기"}
                        />
                    </Box>
                </Box>
                )}
            </Paper>

            {/* ── 발급 이력 ─────────────────────────────────────────────── */}
            <Paper sx={ds.card}>
                <Box sx={{ px: 2.5, pt: 2, pb: 1.5, display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
                    <Typography sx={{ ...ds.typography.sectionTitle, flex: 1 }}>
                        발급 이력{" "}
                        <span style={{ color: ds.colors.textMuted, fontWeight: 400, fontSize: 13 }}>
                            {totalElements}건
                        </span>
                    </Typography>
                    <Box sx={{ width: 150 }}>
                        <ComboBox
                            clearable={false}
                            options={[
                                { value: "ALL", label: "전체 종류" },
                                { value: "EMPLOYMENT", label: "재직증명서" },
                                { value: "CAREER", label: "경력증명서" },
                            ]}
                            value={typeFilter}
                            onChange={v => { setTypeFilter(v); setPage(1); }}
                        />
                    </Box>
                </Box>
                <Divider />

                {historyLoading ? <LoadingDots /> : (
                    <>
                    <ResizableTable size="medium" sx={ds.tableSx}>
                        <TableHead>
                            <TableRow>
                                <TableCell>발급번호</TableCell>
                                <TableCell>종류</TableCell>
                                <TableCell>대상</TableCell>
                                <TableCell>용도</TableCell>
                                <TableCell>발급일</TableCell>
                                <TableCell>발급자</TableCell>
                                <TableCell align="center">사본</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {history.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={7} align="center" sx={{ py: 4, color: ds.colors.textMuted }}>
                                        발급 이력이 없습니다.
                                    </TableCell>
                                </TableRow>
                            )}
                            {history.map(row => {
                                const meta = CERTIFICATE_TYPE[row.type] || {};
                                return (
                                    <TableRow key={row.id} hover sx={{ cursor: "pointer" }}
                                              onClick={() => handleReissue(row)}>
                                        <TableCell sx={{ fontFamily: "monospace", fontSize: 12, color: ds.colors.textSecondary }}>
                                            {row.certNo}
                                        </TableCell>
                                        <TableCell>
                                            <Chip label={meta.short || row.typeLabel} size="small"
                                                  sx={{ bgcolor: meta.bg, color: meta.color, fontWeight: 700 }} />
                                        </TableCell>
                                        <TableCell>
                                            <Typography sx={{ fontSize: 13, fontWeight: 600, lineHeight: 1.2 }}>
                                                {row.employeeName || "—"}
                                            </Typography>
                                            <Typography sx={{ fontSize: 11.5, color: ds.colors.textMuted, lineHeight: 1.2 }}>
                                                {[row.deptName, row.positionName].filter(Boolean).join(" · ") || "—"}
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{ fontSize: 12.5 }}>{row.purpose}</TableCell>
                                        <TableCell sx={{ fontSize: 12, color: ds.colors.textSecondary }}>
                                            {fmtDate(row.issuedDate)}
                                            <Typography sx={{ fontSize: 11, color: ds.colors.textMuted, lineHeight: 1.2 }}>
                                                {fmtDateTime(row.issuedAt)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{ fontSize: 12, color: ds.colors.textSecondary }}>
                                            {row.issuedByName || "—"}
                                        </TableCell>
                                        <TableCell align="center">
                                            <ActionButton
                                                variant="secondary"
                                                startIcon={<DownloadIcon sx={{ fontSize: 14 }} />}
                                                loading={downloadingId === row.id}
                                                onClick={(e) => { e.stopPropagation(); handleReissue(row); }}
                                                sx={{ fontSize: 11.5, py: 0.25 }}
                                                label="사본"
                                            />
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </ResizableTable>
                    <TablePaginator
                        totalPages={totalPages}
                        total={totalElements}
                        pageSize={pageSize}
                        page={page}
                        onPageChange={setPage}
                    />
                    </>
                )}
            </Paper>
        </Box>
    );
}
